import { Link } from "react-router-dom";
import { GraduationCap, ArrowLeft, ShieldCheck, Heart, Users, Lock } from "lucide-react";
import Footer from "@/components/Footer";
import useDocumentMeta from "@/hooks/useDocumentMeta";

/**
 * Student Data Privacy — district-ready summary page.
 * Optimized for procurement/IT reviewers to scan in 60 seconds.
 */
export default function StudentPrivacyPage() {
  useDocumentMeta({
    title: "Student Data Privacy | Lemonade Stand Business Plan",
    description:
      "Plain-language student data privacy summary for schools, districts, and parents — FERPA, COPPA, responsible AI, data minimization, and vendor transparency.",
    keywords:
      "student data privacy, FERPA, COPPA, K-12 educational technology, responsible AI learning, digital citizenship",
  });

  const promises = [
    { icon: <Lock className="w-5 h-5" />, label: "We never sell student data — full stop." },
    { icon: <ShieldCheck className="w-5 h-5" />, label: "We do not use student data to train AI models." },
    { icon: <Heart className="w-5 h-5" />, label: "We minimize what we collect — name, email, lesson responses. That's it." },
    { icon: <Users className="w-5 h-5" />, label: "We sign your district's Student Data Privacy Agreement on request." },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-yellow-50 to-white flex flex-col">
      <div className="max-w-4xl mx-auto px-4 py-10 sm:py-14 flex-1 w-full">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium text-sm mb-6"
          data-testid="student-privacy-back-link"
        >
          <ArrowLeft className="w-4 h-4" /> Back to home
        </Link>

        <header className="mb-8">
          <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-800 px-3 py-1.5 rounded-full text-xs font-semibold mb-4">
            <GraduationCap className="w-3.5 h-3.5" /> For Schools, Districts &amp; Families
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
            Student Data Privacy
          </h1>
          <p className="text-lg text-gray-700 max-w-2xl">
            Plain-language commitments to parents, educators, and district
            reviewers about how we handle student information.
          </p>
        </header>

        {/* Promise grid */}
        <section className="grid sm:grid-cols-2 gap-3 mb-10">
          {promises.map((p) => (
            <div
              key={p.label}
              className="bg-white ring-1 ring-emerald-200 rounded-2xl p-4 flex items-start gap-3 shadow-sm"
            >
              <span className="bg-emerald-500 text-white w-9 h-9 rounded-xl flex items-center justify-center shrink-0">
                {p.icon}
              </span>
              <p className="text-sm font-semibold text-gray-900 leading-snug pt-1.5">
                {p.label}
              </p>
            </div>
          ))}
        </section>

        <section className="prose prose-sm sm:prose-base max-w-none text-gray-800 leading-relaxed space-y-6">
          <h2 className="text-xl font-bold text-gray-900">FERPA</h2>
          <p>
            When a school or district uses the Lemonade Stand Business Plan,
            we operate as a <strong>"school official"</strong> under FERPA
            (34 CFR § 99.31(a)(1)). That means the school directs how student
            education records are used, we only use them to deliver the
            service the school requested, and we don't disclose them to anyone
            the school hasn't authorized.
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Schools can export student records on demand (CSV / PDF).</li>
            <li>Schools can request deletion; we complete it within 30 days.</li>
            <li>
              We will sign your district's Data Privacy Agreement (DPA) —
              including SDPC/NDPA standard contracts — at no cost.
            </li>
          </ul>

          <h2 className="text-xl font-bold text-gray-900">COPPA (under 13)</h2>
          <p>
            For students under 13, we require <strong>verifiable consent</strong>{" "}
            from a parent, legal guardian, or school acting under FERPA. We
            collect only the information necessary to deliver the lesson:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Name (first name is fine)</li>
            <li>Email (a parent's or school-provisioned address)</li>
            <li>Lesson responses and Mini Business Plan content</li>
          </ul>
          <p>
            Our Elementary preview pathway (grades 2-5) does not currently
            collect any data — all progress is stored locally in the child's
            own browser.
          </p>

          <h2 className="text-xl font-bold text-gray-900">Responsible AI</h2>
          <p>
            LemonBot is our AI coach (OpenAI GPT-4o under the hood). It's
            designed to ask questions — not write a student's answers for
            them. Key safeguards:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Conversations run under OpenAI's enterprise terms (no training on customer data).</li>
            <li>We do not use student chats to train any AI model.</li>
            <li>Educators can disable LemonBot for any class with one click.</li>
            <li>
              Daily per-user quotas (100 chats / 40 voice replies) keep
              classroom use focused.
            </li>
          </ul>

          <h2 className="text-xl font-bold text-gray-900">Where data lives</h2>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Hosting:</strong> Emergent Cloud (U.S. Kubernetes).</li>
            <li><strong>Database:</strong> MongoDB Atlas (U.S. region).</li>
            <li><strong>AI:</strong> OpenAI Enterprise API.</li>
            <li><strong>Email:</strong> SendGrid (transactional only).</li>
          </ul>
          <p>
            See our full{" "}
            <Link to="/subprocessors" className="text-yellow-700 underline">
              Subprocessors list
            </Link>{" "}
            for current vendors.
          </p>

          <h2 className="text-xl font-bold text-gray-900">Security at a glance</h2>
          <ul className="list-disc pl-6 space-y-1">
            <li>HTTPS / TLS 1.2+ on every connection.</li>
            <li>bcrypt password hashing.</li>
            <li>Rate-limiting against brute-force login attempts.</li>
            <li>JWT-based session tokens with rotation.</li>
            <li>Sentry error tracking with PII redaction.</li>
            <li>Annual security review (next review due Q3 2026).</li>
          </ul>

          <h2 className="text-xl font-bold text-gray-900">Need a DPA or vendor questionnaire?</h2>
          <p>
            Email{" "}
            <a href="mailto:privacy@thewementality.com" className="text-yellow-700 underline">
              privacy@thewementality.com
            </a>
            . We respond to district vendor-review requests within 2 business
            days.
          </p>

          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5 mt-8">
            <p className="text-sm font-semibold text-emerald-900 mb-2">
              Related documents
            </p>
            <ul className="text-sm space-y-1.5">
              <li>
                <Link to="/privacy" className="text-emerald-800 underline font-medium">
                  Full Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/terms" className="text-emerald-800 underline font-medium">
                  Terms of Service
                </Link>
              </li>
              <li>
                <Link to="/subprocessors" className="text-emerald-800 underline font-medium">
                  Subprocessors List
                </Link>
              </li>
            </ul>
          </div>
        </section>
      </div>
      <Footer />
    </div>
  );
}
